import * as Sentry from "@sentry/react";
import {endOfDay, startOfDay} from "date-fns";
import {type ButtonHTMLAttributes} from "react";
import {DateRange, DayPicker, DropdownProps} from "react-day-picker";
import {FiChevronLeft, FiChevronRight} from "react-icons/fi";

import {DatePickerDayPickerProps} from "@/components/datePicker/subComponents/DatePickerDayPicker/DatePickerDayPicker.types.ts";
import {DatePickerSelect} from "@/components/datePicker/subComponents/DatePickerDayPicker/subComponents/DatePickerSelect/DatePickerSelect.tsx";
import {
  DayPickerButton,
  DayPickerButtonProps,
} from "@/components/datePicker/subComponents/DatePickerDayPicker/subComponents/DayPickerButton/DayPickerButton.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";

import "react-day-picker/style.css";
import styles from "@/components/datePicker/subComponents/DatePickerDayPicker/DatePickerDayPicker.module.scss";

export const DatePickerDayPicker = ({
  mode,
  animate = true,
  showOutsideDays = true,
  navLayout = "around",
  captionLayout = "dropdown",
  selected,
  splitRole,
  onChange,
  endMonth = new Date(2099, 1),
  ...rest
}: Omit<DatePickerDayPickerProps, "onSelect">) => {
  if (mode === "split" && !splitRole) {
    Sentry.captureException(new Error("For split mode, splitRole must be provided."));
    splitRole = "start";
  }
  let defaultMonth: Date | undefined;
  if (mode === "single" && selected) {
    defaultMonth = selected as Date;
  } else if ((mode === "range" || mode === "split") && selected) {
    const range = selected as DateRange;
    if (splitRole === "end") {
      defaultMonth = range.to || undefined;
    } else {
      defaultMonth = range.from || undefined;
    }
  }

  const dayPickerProps = {
    animate,
    navLayout,
    showOutsideDays,
    captionLayout,
    endMonth,
    classNames: {
      weekday: styles.datePickerWeekday,
      day: styles.datePickerDay,
      day_button: styles.datePickerDayButton,
      month_grid: styles.datePickerMonth,
      dropdowns: styles.dropdowns,
    },
    modifiersClassNames: {
      today: styles.datePickerModifier,
      selected: styles.datePickerModifier,
      range_start: styles.datePickerModifier,
      range_middle: styles.datePickerModifier,
      range_end: styles.datePickerModifier,
    },
    components: {
      NextMonthButton: (props: ButtonHTMLAttributes<HTMLButtonElement>) => (
        <IconButton icon={FiChevronRight} size={"fitIcon"} variant={"ghost"} {...props} />
      ),
      PreviousMonthButton: (props: ButtonHTMLAttributes<HTMLButtonElement>) => (
        <IconButton icon={FiChevronLeft} size={"fitIcon"} variant={"ghost"} {...props} />
      ),
      YearsDropdown: (props: DropdownProps) => <DatePickerSelect {...props} />,
      MonthsDropdown: (props: DropdownProps) => <DatePickerSelect variant={"datepickerMonths"} {...props} />,
      DayButton: (props: DayPickerButtonProps) => <DayPickerButton {...props} />,
    },
    defaultMonth: defaultMonth,
    ...rest,
  };

  //https://github.com/gpbl/react-day-picker/discussions/1570#discussioncomment-10511725 🤢
  return (
    <>
      {mode === "range" && (
        <DayPicker
          {...dayPickerProps}
          selected={selected as DateRange | undefined}
          mode={"range"}
          onSelect={() => {}}
          onDayClick={(day) => {
            if (mode === "range") {
              const range = selected as DateRange | undefined;
              if (!range?.from) {
                if (range?.to && range.to < day) {
                  onChange?.({from: startOfDay(range.to), to: endOfDay(day)} as DateRange);
                  return;
                }
                onChange?.({from: day, to: range?.to} as DateRange);
                return;
              }
              if (!range?.to) {
                if (range?.from && range.from > day) {
                  onChange?.({from: day, to: endOfDay(range.from)} as DateRange);
                  return;
                }
                onChange?.({from: range?.from, to: endOfDay(day)} as DateRange);
                return;
              }
              onChange?.({from: day, to: undefined} as DateRange);
            }
          }}
        />
      )}
      {mode === "split" && (
        <DayPicker
          {...dayPickerProps}
          mode={"range"}
          onSelect={() => {}}
          selected={selected as DateRange | undefined}
          onDayClick={(day) => {
            const range = selected as DateRange | undefined;
            if (splitRole === "start") {
              const selectedDay = startOfDay(day);
              if (range?.from) {
                selectedDay.setHours(range.from.getHours(), range.from.getMinutes());
              }
              onChange?.({from: selectedDay, to: range?.to});
            } else if (splitRole === "end") {
              const selectedDay = endOfDay(day);
              if (range?.to) {
                selectedDay.setHours(range.to.getHours(), range.to.getMinutes());
              }
              onChange?.({from: range?.from, to: selectedDay});
            }
          }}
        />
      )}
      {mode === "single" && (
        <DayPicker
          {...dayPickerProps}
          mode={"single"}
          selected={selected as Date | undefined}
          onSelect={(date: Date | undefined) => {
            const selectedDate = selected as Date | undefined;
            if (!date) {
              onChange?.(undefined);
              return;
            }
            if (selectedDate) {
              date.setHours(selectedDate.getHours(), selectedDate.getMinutes());
            }
            onChange?.(date as Date | undefined);
          }}
        />
      )}
    </>
  );
};
